import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  GlobalAppState,
  SearchBarState,
  HomepageState,
  ModalState,
  PayloadPaginationType,
  VideoOrderEnum,
  RegionCodeEnum,
  VideoTypeEnum,
  MaxResultsEnum,
} from '../types/types';
import { getApiData } from '../utils/data';

export const initialState: GlobalAppState = {
  search: '',
  showModal: false,
  pageNumber: 1,
  currPageToken: null,
  homePageState: {
    isLoaded: false,
    cards: [],
    hasError: false,
    prevPageToken: null,
    nextPageToken: null,
    totalResults: null,
    resultsPerPage: null,
  },
  searchBarState: {
    videoOrder: VideoOrderEnum.viewCount,
    regionCode: RegionCodeEnum.US,
    videoType: VideoTypeEnum.video,
    maxResults: MaxResultsEnum.first,
  },
  modalState: {
    isModalLoaded: false,
    modalCard: [],
    hasModalError: false,
  },
};

type ApiDataArgsType = {
  search: string;
  isSingleCard?: boolean;
  searchBarState?: SearchBarState;
  currPageToken?: string | null;
};
type ApiModalDataArgsType = {
  cardID: string;
  isSingleCard?: boolean;
};

export const fetchCardsData = createAsyncThunk(
  'fetchCardsData',
  async (apiDataArgs: ApiDataArgsType) => {
    const { search, isSingleCard, searchBarState, currPageToken } = apiDataArgs;
    const apiData = await getApiData(search, isSingleCard, searchBarState, currPageToken);
    return apiData;
  }
);

export const fetchModalCardData = createAsyncThunk(
  'fetchModalCardData',
  async (apiDataArgs: ApiModalDataArgsType) => {
    const { cardID, isSingleCard } = apiDataArgs;
    const apiData = await getApiData(cardID, isSingleCard);
    return apiData;
  }
);

const slice = createSlice({
  name: 'apiPageReducer',
  initialState: initialState as GlobalAppState,
  reducers: {
    updateSearch(state: GlobalAppState, { payload }: PayloadAction<string>) {
      state.search = payload;
    },
    toggleModal(state: GlobalAppState, { payload }: PayloadAction<boolean>) {
      state.showModal = payload;
    },
    updateHomepage(state: GlobalAppState, { payload }: PayloadAction<Partial<HomepageState>>) {
      state.homePageState = { ...state.homePageState, ...payload };
    },
    updateSearchBar(state: GlobalAppState, { payload }: PayloadAction<Partial<SearchBarState>>) {
      state.searchBarState = { ...state.searchBarState, ...payload };
    },
    updateModal(state: GlobalAppState, { payload }: PayloadAction<Partial<ModalState>>) {
      state.modalState = { ...state.modalState, ...payload };
    },
    updatePagination(state: GlobalAppState, { payload }: PayloadAction<PayloadPaginationType>) {
      state.pageNumber = payload.pageNumber;
      state.currPageToken = payload.pageToken;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCardsData.pending, (state) => {
      state.homePageState.isLoaded = false;
    }),
      builder.addCase(fetchCardsData.fulfilled, (state, action) => {
        state.homePageState.isLoaded = true;
        state.homePageState = action.payload as HomepageState;
      }),
      builder.addCase(fetchModalCardData.fulfilled, (state, action) => {
        state.modalState = action.payload as ModalState;
      });
  },
});

export const apiPageReducer = slice.reducer;
export const {
  updateSearch,
  updateHomepage,
  updateModal,
  updatePagination,
  updateSearchBar,
  toggleModal,
} = slice.actions;
